/* eslint-disable camelcase */
import { getBlockCheckoutCondition } from './PricingUtils';
import type {
  DesignLicenseTagConfigType,
  IDesignLicenses,
  IDesignPricing,
} from '@/common/interfaces/design.interface';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { DesignLicenseStatusTypes } from '@/constants/Proof';
import {
  NEEDS_CHANGES_REJECTION_MESSAGES,
  designLicenseComments,
  designLicenseTagConfig,
} from '@/constants/DesignLicense';
import {
  LICENSING_LABEL_DISABLE_CART,
  LICENSING_STATUS_PILL_STYLES,
} from '@/constants/LicensingStatus';

/**
 * Method to return the licensing review comment in checkout page.
 *
 * @param desLicensingRecords - Design licensing records
 * @param userOrg - logged in user organization
 */
export const getCheckOutLicensingComment = (
  desLicensingRecords: IDesignLicenses[],
  userOrg: string
) => {
  let licensingInfo: DesignLicenseTagConfigType | null = null;
  let collgStatuses: DesignLicenseStatusTypes[] = [];
  let greekStatuses: DesignLicenseStatusTypes[] = [];
  let schoolOrNationals = '';
  let reviewComment = '';

  const reviewStatusTypes: DesignLicenseStatusTypes[] = [
    'will_require_approval',
    'marks_in_review',
    'approval_required',
  ];

  if (desLicensingRecords?.length) {
    collgStatuses = desLicensingRecords.map((rec) => rec?.collegiate_status);
    greekStatuses = desLicensingRecords.map((rec) => rec?.greek_status);

    const isCollgStatusInReview = reviewStatusTypes?.some((licenStatus: DesignLicenseStatusTypes) =>
      collgStatuses?.includes(licenStatus)
    );
    const isGreekStatusInReview = reviewStatusTypes?.some((licenStatus: DesignLicenseStatusTypes) =>
      greekStatuses?.includes(licenStatus)
    );

    reviewComment =
      isCollgStatusInReview || isGreekStatusInReview
        ? `${designLicenseComments.licensing_review}`
        : '';

    if (isCollgStatusInReview && !isGreekStatusInReview) {
      schoolOrNationals = 'your School';
    } else if (isGreekStatusInReview && !isCollgStatusInReview) {
      schoolOrNationals = '[Org] Nationals';
    } else if (isCollgStatusInReview && isGreekStatusInReview) {
      schoolOrNationals = '[Org] Nationals & your school';
    }

    reviewComment = schoolOrNationals && reviewComment?.replaceAll('Nationals', schoolOrNationals);

    /** Adding the org name for a collegiate client */
    if (userOrg && reviewComment?.includes('[Org]')) {
      reviewComment = reviewComment.replaceAll('[Org]', userOrg);
    } else if (!userOrg && reviewComment?.includes('[Org]')) {
      reviewComment = reviewComment?.replaceAll('[Org]', '');
    }
  }

  if (reviewComment) {
    licensingInfo = {
      image: designLicenseTagConfig.licensing_review.image,
      theme: designLicenseTagConfig.licensing_review.theme,
      label: designLicenseTagConfig.licensing_review.label,
      popoverStyles: LICENSING_STATUS_PILL_STYLES.licensing_review,
      comment: reviewComment,
    };
  }

  return licensingInfo;
};

/**
 * Method to get the license rejection comment for the design.
 *
 * @param collegiateStatus - The status of the collegiate licensing
 * @param greekStatus - The status of the greek licensing
 * @param isDesignBlockedForCheckout - Whether the design is blocked for checkout
 * @param userOrg - The user organization
 */
export const getDesignLicensingRejectionComment = (
  collegiateStatus: DesignLicenseStatusTypes,
  greekStatus: DesignLicenseStatusTypes,
  isDesignBlockedForCheckout: boolean,
  userOrg?: string
) => {
  let comment = '';
  let checkoutComment = '';

  if (isDesignBlockedForCheckout) {
    comment = NEEDS_CHANGES_REJECTION_MESSAGES.block;
    checkoutComment = NEEDS_CHANGES_REJECTION_MESSAGES.block;

    return { comment, checkoutComment };
  }

  if (
    LICENSING_LABEL_DISABLE_CART.includes(collegiateStatus) &&
    LICENSING_LABEL_DISABLE_CART.includes(greekStatus)
  ) {
    const commentValue = NEEDS_CHANGES_REJECTION_MESSAGES.both;
    comment = commentValue.replaceAll('Nationals', userOrg || '');
    const checkoutCommentValue = NEEDS_CHANGES_REJECTION_MESSAGES.bothCheckout;
    checkoutComment = checkoutCommentValue.replaceAll('Nationals', userOrg || '');

    return { comment, checkoutComment };
  }

  if (
    LICENSING_LABEL_DISABLE_CART.includes(collegiateStatus) &&
    !LICENSING_LABEL_DISABLE_CART.includes(greekStatus)
  ) {
    comment = NEEDS_CHANGES_REJECTION_MESSAGES.collegiate;
    checkoutComment = NEEDS_CHANGES_REJECTION_MESSAGES.collegiateCheckout;

    return { comment, checkoutComment };
  }

  if (
    LICENSING_LABEL_DISABLE_CART.includes(greekStatus) &&
    !LICENSING_LABEL_DISABLE_CART.includes(collegiateStatus)
  ) {
    const commentValue = NEEDS_CHANGES_REJECTION_MESSAGES.greek;
    comment = commentValue.replaceAll('Nationals', userOrg || '');
    const checkoutCommentValue = NEEDS_CHANGES_REJECTION_MESSAGES.greekCheckout;
    checkoutComment = checkoutCommentValue.replaceAll('Nationals', userOrg || '');

    return { comment, checkoutComment };
  }

  return { comment, checkoutComment };
};

/**
 * Method called to check if the design is rejected for checkout.
 *
 * @param fromDesignTool - The design is from design tool
 * @param designLicensing - The design licensing
 * @param designPricing - The design pricing
 * @param userOrg - The user organization
 */
export const isDesignRejected = (
  fromDesignTool: boolean | number,
  designLicensing: IDesignLicenses,
  designPricing: IDesignPricing,
  userOrg?: string
) => {
  const collegiateStatus = designLicensing?.collegiate_status ?? 'no_marks';
  const greekStatus = designLicensing?.greek_status ?? 'no_marks';
  const isDesignRejectedForCheckout =
    LICENSING_LABEL_DISABLE_CART.includes(collegiateStatus) ||
    LICENSING_LABEL_DISABLE_CART.includes(greekStatus);

  const designPricingRecord = designPricing as IDesignPricing;
  const blockSelfCheckout = designPricingRecord?.block_self_checkout;
  const isDesignBlockedForCheckout = getBlockCheckoutCondition(
    Boolean(fromDesignTool),
    blockSelfCheckout,
    designPricing
  );

  const { comment, checkoutComment } = getDesignLicensingRejectionComment(
    collegiateStatus,
    greekStatus,
    isDesignBlockedForCheckout,
    userOrg
  );

  /**
   * If the user is a collegiate user then only consider isDesignRejectedForCheckout.
   */

  return {
    isDesignLicensingRejected: userOrg ? isDesignRejectedForCheckout : false,
    isDesignSelfBlockedForCheckout: isDesignBlockedForCheckout,
    comment,
    checkoutComment,
  };
};

/** Attach rejection/block-self-checkout info used by store cart & checkout UIs. */
export const withDesignRejectionInfo = (
  item: ICartItemDetail,
  userOrg?: string
): ICartItemDetail => ({
  ...item,
  designRejectionInfo: isDesignRejected(
    item.fromDesignTool,
    item.designLicensing,
    item.designPricing,
    userOrg
  ),
});

export const withDesignRejectionInfoList = (
  items: ICartItemDetail[],
  userOrg?: string
): ICartItemDetail[] => items.map((item) => withDesignRejectionInfo(item, userOrg));
